import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { FieldLabel } from '../ui/field-label'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'
import type { CaseReviewerPickerOption, CaseReviewerPickerResponse } from '../../types/case'

const MAX_CASE_REVIEWERS = 2

type CaseReviewerEditSectionProps = {
  picker: CaseReviewerPickerResponse | null
  reviewerIds: [string, string]
  onChange: (ids: [string, string]) => void
  disabled?: boolean
}

export function CaseReviewerEditSection({
  picker,
  reviewerIds,
  onChange,
  disabled = false,
}: CaseReviewerEditSectionProps) {
  const [profileDoctor, setProfileDoctor] = useState<CaseReviewerPickerOption | null>(null)

  const doctors = picker?.doctors ?? []

  const optionsFor = (slot: 0 | 1): CaseReviewerPickerOption[] => {
    const otherId = reviewerIds[slot === 0 ? 1 : 0]
    return doctors.filter((doctor) => doctor.id !== otherId)
  }

  const findDoctor = (id: string) => doctors.find((doctor) => doctor.id === id) ?? null

  const setSlot = (slot: 0 | 1, value: string) => {
    const next: [string, string] = [...reviewerIds]
    next[slot] = value
    onChange(next)
  }

  return (
    <fieldset className="space-y-4 rounded-md border border-parch-100 bg-parch-50/60 p-4">
      <legend className="px-1 text-sm font-semibold text-ink-800">Case reviewers</legend>
      <p className="text-xs text-ink-500">
        Reviewers are optional (up to {MAX_CASE_REVIEWERS} approved doctors). Assistants cannot be
        case reviewers. Selecting at least one opens review when you save. After you close review,
        reviewers become read-only. Open the dropdown and use <strong>Profile</strong> next to a
        doctor name to preview them.
      </p>

      {([0, 1] as const).map((slot) => (
        <div key={slot} className="space-y-2">
          <FieldLabel optional>{slot === 0 ? 'Reviewer 1' : 'Reviewer 2'}</FieldLabel>
          <DoctorReviewerSelect
            doctors={optionsFor(slot)}
            value={reviewerIds[slot]}
            selected={findDoctor(reviewerIds[slot])}
            disabled={disabled}
            onChange={(value) => setSlot(slot, value)}
            onShowProfile={setProfileDoctor}
          />
        </div>
      ))}

      {profileDoctor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-card">
            <h3 className="font-display text-lg font-bold text-ink-900">{profileDoctor.name}</h3>
            <p className="mt-1 text-sm text-ink-500">{profileDoctor.email}</p>
            <dl className="mt-4 space-y-2 text-sm">
              <ProfileRow label="Registration number" value={profileDoctor.registration_number} />
              <ProfileRow label="Department" value={profileDoctor.department_name} />
              <ProfileRow
                label="Hospitals"
                value={
                  profileDoctor.hospitals && profileDoctor.hospitals.length > 0
                    ? profileDoctor.hospitals.join(', ')
                    : null
                }
              />
              <ProfileRow
                label="Owned cases"
                value={
                  profileDoctor.owned_cases_count != null
                    ? String(profileDoctor.owned_cases_count)
                    : null
                }
              />
              <ProfileRow
                label="Public cases"
                value={
                  profileDoctor.public_cases_count != null
                    ? String(profileDoctor.public_cases_count)
                    : null
                }
              />
            </dl>
            <div className="mt-6 flex justify-end">
              <Button type="button" variant="secondary" onClick={() => setProfileDoctor(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </fieldset>
  )
}

type DoctorReviewerSelectProps = {
  doctors: CaseReviewerPickerOption[]
  value: string
  selected: CaseReviewerPickerOption | null
  disabled?: boolean
  onChange: (value: string) => void
  onShowProfile: (doctor: CaseReviewerPickerOption) => void
}

function DoctorReviewerSelect({
  doctors,
  value,
  selected,
  disabled = false,
  onChange,
  onShowProfile,
}: DoctorReviewerSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-parch-200 bg-white px-3 py-2 text-left text-sm text-ink-900 hover:bg-parch-50 disabled:cursor-not-allowed disabled:opacity-50',
        )}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={cn('truncate', !value && 'text-ink-500')}>
          {selected?.name ?? 'Select doctor'}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-ink-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute left-0 z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-parch-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            className={cn(
              'flex w-full px-3 py-2 text-left text-sm transition-colors',
              !value ? 'bg-green-50 text-green-800' : 'text-ink-800 hover:bg-parch-50',
            )}
            onClick={() => {
              onChange('')
              setOpen(false)
            }}
          >
            None
          </button>

          {doctors.map((doctor) => {
            const isSelected = value === doctor.id
            return (
              <div
                key={doctor.id}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-sm',
                  isSelected ? 'bg-green-50 text-green-800' : 'text-ink-800 hover:bg-parch-50',
                )}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left"
                  onClick={() => {
                    onChange(doctor.id)
                    setOpen(false)
                  }}
                >
                  {doctor.name}
                </button>
                <button
                  type="button"
                  className="shrink-0 text-xs font-semibold text-green-700 hover:underline"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    onShowProfile(doctor)
                  }}
                >
                  Profile
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function ProfileRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-4 border-b border-parch-100 pb-2">
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-right font-medium text-ink-800">{value?.trim() || '—'}</dd>
    </div>
  )
}

export function reviewerIdsFromSlots(slots: [string, string]): string[] {
  return slots.filter(Boolean).slice(0, MAX_CASE_REVIEWERS)
}

export function slotsFromReviewerIds(ids: string[]): [string, string] {
  return [ids[0] ?? '', ids[1] ?? '']
}

export function useDoctorReviewerOptions(picker: CaseReviewerPickerResponse | null) {
  return useMemo(() => picker?.doctors ?? [], [picker])
}
